import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { FichaItem } from '../types';

interface Props {
  ficha: FichaItem[];
  temFormulario: boolean;
}

export default function AbaFicha({ ficha, temFormulario }: Props) {
  const [fotoUrls, setFotoUrls] = useState<Record<string, string>>({});
  const [ampliada, setAmpliada] = useState<string | null>(null);
  const [colapsados, setColapsados] = useState<Set<number>>(new Set());

  const caminhos = useMemo(
    () =>
      [
        ...new Set(
          ficha
            .filter((item) => item.tipo === 'foto' && item.resposta_texto)
            .map((item) => item.resposta_texto as string),
        ),
      ],
    [ficha],
  );

  useEffect(() => {
    let ativo = true;
    if (caminhos.length === 0) {
      setFotoUrls({});
      return;
    }
    supabase.storage
      .from('fichas')
      .createSignedUrls(caminhos, 3600)
      .then(({ data }) => {
        if (!ativo) return;
        const mapa: Record<string, string> = {};
        (data || []).forEach((s) => {
          if (s.signedUrl) mapa[s.path] = s.signedUrl;
        });
        setFotoUrls(mapa);
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [caminhos]);

  function alternar(idx: number) {
    setColapsados((prev) => {
      const novo = new Set(prev);
      if (novo.has(idx)) novo.delete(idx);
      else novo.add(idx);
      return novo;
    });
  }

  if (!temFormulario) {
    return (
      <p className="py-6 text-[15px] text-foreground-500">Esta participação não tem ficha.</p>
    );
  }

  if (ficha.length === 0) {
    return <p className="py-6 text-[15px] text-foreground-500">Sem respostas registradas.</p>;
  }

  return (
    <div className="space-y-3">
      {ficha.map((item, idx) => {
        const ehFoto = item.tipo === 'foto';
        const url = ehFoto && item.resposta_texto ? fotoUrls[item.resposta_texto] : null;
        const aberto = !colapsados.has(idx);

        return (
          <div key={`${idx}-${item.ordem}`} className="rounded-2xl bg-background-50 p-4">
            <button
              type="button"
              onClick={() => alternar(idx)}
              className="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
            >
              <span className="text-[13px] font-medium text-foreground-500">{item.enunciado}</span>
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400">
                <i className={aberto ? 'ri-arrow-up-s-line text-lg' : 'ri-arrow-down-s-line text-lg'} />
              </span>
            </button>

            {aberto &&
              (ehFoto ? (
                <div className="mt-2">
                  {url ? (
                    <button
                      type="button"
                      onClick={() => setAmpliada(url)}
                      className="block h-40 w-40 cursor-pointer overflow-hidden rounded-2xl bg-white"
                      aria-label="Ampliar foto"
                    >
                      <img
                        src={url}
                        alt="Foto enviada pelo participante"
                        className="h-full w-full object-contain object-center"
                      />
                    </button>
                  ) : (
                    <div className="flex h-40 w-40 items-center justify-center rounded-2xl border border-dashed border-secondary-300 bg-white text-secondary-400">
                      <i className="ri-image-line text-3xl" />
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-2 text-[15px] font-medium text-foreground-950">
                  {item.resposta_texto || '—'}
                </p>
              ))}
          </div>
        );
      })}

      {ampliada && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 px-4 py-8"
          onClick={() => setAmpliada(null)}
        >
          <div className="relative max-h-full max-w-full">
            <img
              src={ampliada}
              alt="Foto ampliada"
              className="max-h-[80vh] max-w-full rounded-lg object-contain"
            />
            <button
              type="button"
              onClick={() => setAmpliada(null)}
              aria-label="Fechar"
              className="absolute -right-3 -top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white text-foreground-900 shadow-sm"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}